import { useEffect, useRef, useState } from "react";
import {
  ANALISES_OLEO,
  CAMPOS_PLACA,
  COLETA_APLICA,
  ETAPAS_INFO,
  INSPECAO_FINAL,
  RESPOSTAS_INSPECAO,
  RESULTADOS_ENSAIO,
  SERVICOS_PADRAO,
  hojeYmd,
  indiceEtapa,
  nomeEtapa,
  type ColetaOleo,
  type EntradaTrafo,
  type Ensaio,
  type ItemInspecao,
  type Servico,
} from "../trafos";
import { prepararFoto, FotoInvalida } from "../foto";
import { IconCamera, IconClose, IconPlus } from "../icons";

/**
 * Os formulários das etapas do Controle de Trafo em Reforma (ver
 * TrafoFicha.tsx, que monta a ficha, salva e avança). Aqui só há tela: cada
 * formulário recebe o rascunho e devolve a alteração — quem grava é a ficha.
 *
 * ARQUIVO IGUAL no sistemaColaborador e no sistemaOperacional.
 */

/* ------------------------------------------------------ peças comuns --- */

/** Cor de cada resposta. Vermelho só para o que é problema de verdade. */
export function tomDe(valor: string): string {
  switch (valor) {
    case "Aprovado":
    case "Conforme":
    case "Concluído":
    case "Sim":
      return "ok";
    case "Reprovado":
    case "Não conforme":
      return "danger";
    case "Em andamento":
      return "andamento";
    default:
      return "neutro";
  }
}

/** Botões de escolha única (resultado do ensaio, resposta da inspeção, status). */
export function Opcoes({
  opcoes,
  valor,
  onEscolher,
  travado = false,
  compacto = false,
}: {
  opcoes: readonly string[];
  valor: string;
  onEscolher: (v: string) => void;
  travado?: boolean;
  compacto?: boolean;
}) {
  return (
    <div className={`trafo-opcoes${compacto ? " compacto" : ""}`} role="radiogroup">
      {opcoes.map((op) => (
        <button
          key={op}
          type="button"
          role="radio"
          aria-checked={valor === op}
          className={`trafo-opcao${valor === op ? ` on tom-${tomDe(op)}` : ""}`}
          onClick={() => onEscolher(op)}
          disabled={travado}
        >
          {op}
        </button>
      ))}
    </div>
  );
}

/** Uma pílula de marcar/desmarcar (serviços, análises de óleo). */
function Marcador({
  rotulo,
  marcado,
  onClick,
  travado = false,
  titulo,
}: {
  rotulo: string;
  marcado: boolean;
  onClick: () => void;
  travado?: boolean;
  titulo?: string;
}) {
  return (
    <button
      type="button"
      className={`trafo-marcador${marcado ? " on" : ""}`}
      aria-pressed={marcado}
      onClick={onClick}
      disabled={travado}
      title={titulo}
    >
      <span className="trafo-marcador-caixa" aria-hidden="true">
        {marcado ? "✓" : ""}
      </span>
      {rotulo}
    </button>
  );
}

/**
 * Tirar foto (abre a câmera no celular) ou anexar da galeria. Reduz cada imagem
 * no próprio aparelho (de 3–5 MB para ~200 KB) e devolve as data URLs prontas.
 */
export function SeletorFotos({
  onFotos,
  onErro,
  travado = false,
  ocupado = false,
}: {
  onFotos: (urls: string[]) => void | Promise<void>;
  onErro: (msg: string) => void;
  travado?: boolean;
  ocupado?: boolean;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const arquivo = useRef<HTMLInputElement>(null);
  const [preparando, setPreparando] = useState(false);

  async function receber(lista: FileList | null) {
    if (!lista || lista.length === 0) return;
    setPreparando(true);
    try {
      const prontas: string[] = [];
      for (const arq of Array.from(lista).slice(0, 10)) {
        try {
          prontas.push(await prepararFoto(arq));
        } catch (err) {
          onErro(err instanceof FotoInvalida ? err.message : `Falha ao ler "${arq.name}".`);
        }
      }
      if (prontas.length > 0) await onFotos(prontas);
    } finally {
      setPreparando(false);
    }
  }

  const bloqueado = travado || preparando || ocupado;
  return (
    <div className="trafo-foto-acoes">
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="trafo-input-oculto"
        onChange={(e) => {
          receber(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={arquivo}
        type="file"
        accept="image/*"
        multiple
        className="trafo-input-oculto"
        onChange={(e) => {
          receber(e.target.files);
          e.target.value = "";
        }}
      />
      <button type="button" className="trafo-btn-foto" onClick={() => camera.current?.click()} disabled={bloqueado}>
        <IconCamera size={15} /> Tirar foto
      </button>
      <button
        type="button"
        className="trafo-btn-foto ghost"
        onClick={() => arquivo.current?.click()}
        disabled={bloqueado}
      >
        Anexar
      </button>
      {(preparando || ocupado) && <span className="muted">Enviando foto…</span>}
    </div>
  );
}

/** Foto em tamanho grande (toque na miniatura). */
export function FotoAmpliada({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    // stopPropagation: a foto abre DENTRO da ficha — o toque fora dela não
    // pode chegar ao fundo da ficha, que fecharia a ficha inteira.
    <div
      className="modal-overlay trafo-zoom"
      onMouseDown={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <img src={url} alt="Foto do trafo" onMouseDown={(e) => e.stopPropagation()} />
      <button type="button" className="icon-btn trafo-zoom-fechar" onClick={onClose} aria-label="Fechar">
        <IconClose size={20} />
      </button>
    </div>
  );
}

/* ---------------------------------------------------- rascunhos --- */

/** Ensaios na ordem da lista padrão + os que foram acrescentados à parte. */
export function montarEnsaios(salvos: Ensaio[] | null | undefined, padrao: string[]): Ensaio[] {
  const lista = Array.isArray(salvos) ? salvos : [];
  const porNome = new Map(lista.map((e) => [e.ensaio, e]));
  const out = padrao.map((nome) => porNome.get(nome) ?? { ensaio: nome, resultado: "", valores: "", obs: "" });
  for (const e of lista) if (!padrao.includes(e.ensaio)) out.push(e);
  return out.map((e) => ({ ensaio: e.ensaio, resultado: e.resultado ?? "", valores: e.valores ?? "", obs: e.obs ?? "" }));
}

/** Itens da inspeção final na ordem da lista padrão. */
export function montarInspecao(salvos: ItemInspecao[] | null | undefined): ItemInspecao[] {
  const lista = Array.isArray(salvos) ? salvos : [];
  const porItem = new Map(lista.map((i) => [i.item, i]));
  const out = INSPECAO_FINAL.map((item) => porItem.get(item) ?? { item, resposta: "", obs: "" });
  for (const i of lista) if (!INSPECAO_FINAL.includes(i.item)) out.push(i);
  return out.map((i) => ({ item: i.item, resposta: i.resposta ?? "", obs: i.obs ?? "" }));
}

/** Serviço como está no rascunho da etapa 2 (sem id = ainda não gravado). */
export interface ServicoRascunho {
  id?: string;
  nome: string;
  status?: string;
}

/* ---------------------------------------------------- 1. entrada --- */

export function FormEntrada({
  valor,
  onChange,
}: {
  valor: EntradaTrafo;
  onChange: (v: EntradaTrafo) => void;
}) {
  const set = (campo: keyof EntradaTrafo, v: string) => onChange({ ...valor, [campo]: v });
  return (
    <div className="form-grid trafo-grid">
      <label className="field field-wide">
        <span>
          Cliente <em className="req">*</em>
        </span>
        <input value={valor.cliente} onChange={(e) => set("cliente", e.target.value)} placeholder="Nome do cliente" />
      </label>
      <label className="field">
        <span>OS</span>
        <input value={valor.os_numero} onChange={(e) => set("os_numero", e.target.value)} placeholder="Nº da ordem de serviço" />
      </label>
      <label className="field">
        <span>Nota fiscal de entrada</span>
        <input value={valor.nota_fiscal} onChange={(e) => set("nota_fiscal", e.target.value)} />
      </label>
      <label className="field">
        <span>Identificação do cliente</span>
        <input
          value={valor.identificacao}
          onChange={(e) => set("identificacao", e.target.value)}
          placeholder="TAG / patrimônio"
        />
      </label>
      <label className="field">
        <span>
          Data de entrada <em className="req">*</em>
        </span>
        <input type="date" value={valor.data_entrada} onChange={(e) => set("data_entrada", e.target.value)} />
      </label>
      <label className="field">
        <span>Previsão de entrega</span>
        <input type="date" value={valor.previsao_entrega} onChange={(e) => set("previsao_entrega", e.target.value)} />
      </label>
      <label className="field field-wide">
        <span>Como o trafo chegou</span>
        <textarea
          rows={3}
          value={valor.condicao_chegada}
          onChange={(e) => set("condicao_chegada", e.target.value)}
          placeholder="Avarias, vazamentos, peças faltando, nível de óleo, como veio embalado…"
        />
      </label>
    </div>
  );
}

/* ---------------------------------------------- 2. placa + serviços --- */

export function FormPlaca({
  placa,
  onPlaca,
  servicos,
  onServicos,
  onErro,
}: {
  placa: Record<string, unknown>;
  onPlaca: (p: Record<string, unknown>) => void;
  servicos: ServicoRascunho[];
  onServicos: (s: ServicoRascunho[]) => void;
  onErro: (msg: string) => void;
}) {
  const [outro, setOutro] = useState("");
  const semPlaca = placa.sem_placa === true;
  const set = (k: string, v: unknown) => {
    const novo = { ...placa };
    if (v === "" || v === false || v == null) delete novo[k];
    else novo[k] = v;
    onPlaca(novo);
  };

  const tem = (nome: string) => servicos.some((s) => s.nome.toLowerCase() === nome.toLowerCase());
  function alternar(nome: string) {
    const atual = servicos.find((s) => s.nome.toLowerCase() === nome.toLowerCase());
    if (!atual) {
      onServicos([...servicos, { nome }]);
      return;
    }
    if (atual.status && atual.status !== "Pendente") {
      onErro(`"${atual.nome}" já foi iniciado — para tirar, marque como Cancelado no andamento dos serviços.`);
      return;
    }
    onServicos(servicos.filter((s) => s !== atual));
  }
  function acrescentar() {
    const nome = outro.trim();
    if (!nome) return;
    if (!tem(nome)) onServicos([...servicos, { nome }]);
    setOutro("");
  }
  const extras = servicos.filter((s) => !SERVICOS_PADRAO.some((p) => p.toLowerCase() === s.nome.toLowerCase()));

  return (
    <>
      <h4 className="trafo-secao">Dados da placa de identificação</h4>
      <div className="form-grid trafo-grid">
        {CAMPOS_PLACA.map((c) => {
          const v = String(placa[c.key] ?? "");
          if (c.opcoes && c.opcoes.length <= 3) {
            return (
              <div key={c.key} className="field">
                <span>{c.label}</span>
                <Opcoes opcoes={c.opcoes} valor={v} onEscolher={(op) => set(c.key, op === v ? "" : op)} compacto />
              </div>
            );
          }
          if (c.opcoes) {
            return (
              <label key={c.key} className="field">
                <span>{c.label}</span>
                <select value={v} onChange={(e) => set(c.key, e.target.value)}>
                  <option value="">—</option>
                  {c.opcoes.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </label>
            );
          }
          return (
            <label key={c.key} className="field">
              <span>{c.label}</span>
              <input value={v} onChange={(e) => set(c.key, e.target.value)} placeholder={c.placeholder} />
            </label>
          );
        })}
        <label className="trafo-check field-wide">
          <input type="checkbox" checked={semPlaca} onChange={(e) => set("sem_placa", e.target.checked)} />
          Placa ausente ou ilegível
        </label>
      </div>

      <h4 className="trafo-secao">
        Serviços a executar <span className="muted">({servicos.length} marcado{servicos.length === 1 ? "" : "s"})</span>
      </h4>
      <div className="trafo-marcadores">
        {SERVICOS_PADRAO.map((nome) => {
          const s = servicos.find((x) => x.nome.toLowerCase() === nome.toLowerCase());
          const iniciado = !!s?.status && s.status !== "Pendente";
          return (
            <Marcador
              key={nome}
              rotulo={nome}
              marcado={!!s}
              onClick={() => alternar(nome)}
              titulo={iniciado ? `Já ${s!.status!.toLowerCase()}` : undefined}
            />
          );
        })}
        {extras.map((s) => (
          <Marcador key={s.id ?? s.nome} rotulo={s.nome} marcado onClick={() => alternar(s.nome)} />
        ))}
      </div>
      <div className="trafo-linha-acrescentar">
        <input
          value={outro}
          onChange={(e) => setOutro(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              acrescentar();
            }
          }}
          placeholder="Outro serviço (escreva e toque em Acrescentar)"
        />
        <button type="button" className="btn-secondary btn-sm" onClick={acrescentar} disabled={!outro.trim()}>
          <IconPlus size={14} /> Acrescentar
        </button>
      </div>
    </>
  );
}

/* ---------------------------------------------- ensaios (3 e 5) --- */

export function TabelaEnsaios({
  ensaios,
  onChange,
}: {
  ensaios: Ensaio[];
  onChange: (e: Ensaio[]) => void;
}) {
  const set = (i: number, campo: keyof Ensaio, v: string) =>
    onChange(ensaios.map((e, idx) => (idx === i ? { ...e, [campo]: v } : e)));
  return (
    <div className="trafo-itens">
      {ensaios.map((e, i) => (
        <div key={e.ensaio} className={`trafo-item${e.resultado === "Reprovado" ? " is-problema" : ""}`}>
          <span className="trafo-item-nome">{e.ensaio}</span>
          <Opcoes
            opcoes={RESULTADOS_ENSAIO}
            valor={e.resultado}
            onEscolher={(v) => set(i, "resultado", v === e.resultado ? "" : v)}
            compacto
          />
          <input
            className="trafo-item-campo"
            value={e.valores}
            onChange={(ev) => set(i, "valores", ev.target.value)}
            placeholder="Valores medidos"
          />
          <input
            className="trafo-item-campo"
            value={e.obs}
            onChange={(ev) => set(i, "obs", ev.target.value)}
            placeholder="Observação"
          />
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------- 3. coleta de óleo --- */

export function FormColeta({
  coleta,
  onChange,
  trafoId,
}: {
  coleta: ColetaOleo;
  onChange: (c: ColetaOleo) => void;
  trafoId: number;
}) {
  const set = (campo: keyof ColetaOleo, v: unknown) => onChange({ ...coleta, [campo]: v });
  const analises = coleta.analises ?? [];
  return (
    <>
      <div className="field">
        <span>Houve coleta de óleo?</span>
        <Opcoes
          opcoes={COLETA_APLICA}
          valor={coleta.aplica ?? ""}
          onEscolher={(v) =>
            onChange({
              ...coleta,
              aplica: v,
              // Ao marcar "Sim", a data já vem com hoje (é o caso comum).
              data: v === "Sim" ? coleta.data || hojeYmd() : coleta.data,
            })
          }
          compacto
        />
      </div>
      {coleta.aplica === "Sim" && (
        <div className="form-grid trafo-grid trafo-coleta">
          <label className="field">
            <span>Data da coleta</span>
            <input type="date" value={coleta.data ?? ""} onChange={(e) => set("data", e.target.value)} />
          </label>
          <label className="field">
            <span>Nº do frasco / seringa</span>
            <input value={coleta.amostra ?? ""} onChange={(e) => set("amostra", e.target.value)} />
          </label>
          <label className="field">
            <span>Temperatura do óleo (°C)</span>
            <input value={coleta.temperatura ?? ""} onChange={(e) => set("temperatura", e.target.value)} />
          </label>
          <div className="field field-wide">
            <span>Análises pedidas</span>
            <div className="trafo-marcadores">
              {ANALISES_OLEO.map((a) => (
                <Marcador
                  key={a.key}
                  rotulo={a.label}
                  marcado={analises.includes(a.key)}
                  onClick={() =>
                    set(
                      "analises",
                      analises.includes(a.key) ? analises.filter((x) => x !== a.key) : [...analises, a.key],
                    )
                  }
                />
              ))}
            </div>
          </div>
          <p className="hint field-wide">
            Identifique o frasco ou a seringa com o <strong>Trafo nº {trafoId}</strong> antes de mandar ao Laboratório.
          </p>
        </div>
      )}
      {coleta.aplica && (
        <label className="field field-wide trafo-espaco">
          <span>Observação da coleta</span>
          <input
            value={coleta.obs ?? ""}
            onChange={(e) => set("obs", e.target.value)}
            placeholder={coleta.aplica === "Sim" ? "Aspecto do óleo, ponto de coleta…" : "Por que não houve coleta (ex.: trafo a seco)"}
          />
        </label>
      )}
    </>
  );
}

/* ---------------------------------------------- 5. inspeção final --- */

export function ChecklistInspecao({
  itens,
  onChange,
}: {
  itens: ItemInspecao[];
  onChange: (i: ItemInspecao[]) => void;
}) {
  const set = (i: number, campo: keyof ItemInspecao, v: string) =>
    onChange(itens.map((x, idx) => (idx === i ? { ...x, [campo]: v } : x)));
  const marcarTodos = () =>
    onChange(itens.map((x) => (x.resposta ? x : { ...x, resposta: "Conforme" })));
  const semResposta = itens.filter((x) => !x.resposta).length;
  return (
    <>
      {semResposta > 0 && (
        <div className="trafo-atalho">
          <span className="muted">{semResposta} item(ns) sem resposta.</span>
          <button type="button" className="btn-link" onClick={marcarTodos}>
            Marcar os que faltam como Conforme
          </button>
        </div>
      )}
      <div className="trafo-itens">
        {itens.map((x, i) => (
          <div key={x.item} className={`trafo-item trafo-item-insp${x.resposta === "Não conforme" ? " is-problema" : ""}`}>
            <span className="trafo-item-nome">{x.item}</span>
            <Opcoes
              opcoes={RESPOSTAS_INSPECAO}
              valor={x.resposta}
              onEscolher={(v) => set(i, "resposta", v === x.resposta ? "" : v)}
              compacto
            />
            <input
              className="trafo-item-campo"
              value={x.obs}
              onChange={(e) => set(i, "obs", e.target.value)}
              placeholder={x.resposta === "Não conforme" ? "O que está errado" : "Observação"}
            />
          </div>
        ))}
      </div>
    </>
  );
}

/** Status de um serviço como pílula (lista travada). */
export function PilulaServico({ s }: { s: Servico }) {
  const tom = tomDe(s.status);
  const classe = tom === "ok" ? "pill-ok" : tom === "andamento" ? "pill-andamento" : "pill-neutral";
  return <span className={`pill ${classe}`}>{s.status}</span>;
}

/** As 5 etapas como bolinhas: feitas cheias, a atual em destaque. */
export function Trilha({ etapa }: { etapa: string }) {
  const i = indiceEtapa(etapa);
  return (
    <ol className="trafo-trilha" aria-label={`Etapa: ${nomeEtapa(etapa)}`}>
      {ETAPAS_INFO.map((p, n) => (
        <li key={p.key} className={n < i ? "feito" : n === i ? "atual" : ""} title={`${p.numero}. ${p.curto}`} />
      ))}
    </ol>
  );
}
